<script lang="ts" setup>
import type { HorizontalMenuItem } from '~/types/menus'

defineProps<{
  items: HorizontalMenuItem[]
}>()
</script>

<template>
  <ul class="horizontal-menu flex flex-wrap">
    <li
      v-for="{ backgroundColor, class: className, color, isUppercase, name, path } in items"
      :key="path"
      class="mb-2 mr-2 flex whitespace-nowrap"
      :class="[className, { 'uppercase': isUppercase }, {'animated-button': className === 'green'}]"
    >
      <template v-if="className === 'green'">
        <span /><span /><span /><span />
      </template>

      <NuxtLink :class="{'!border-0': className === 'green'}" :style="{ backgroundColor, color }" :to="path">
        <div>{{ name }}</div>
      </NuxtLink>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
@use 'sass:color';

@use 'assets/css/variables';

.animated-button {
  overflow: hidden;
  position: relative;

  span {
    position: absolute;

    &:nth-child(1) {
      top: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background: -webkit-gradient(linear, right top, left top, from(rgba(43, 8, 8, 0)), to(variables.$main-green-color));
      background: linear-gradient(to left, rgba(43, 8, 8, 0), variables.$main-green-color);
      -webkit-animation: 2s animateTop linear infinite;
              animation: 2s animateTop linear infinite;
    }

    &:nth-child(2) {
      top: 0;
      right: 0;
      height: 100%;
      width: 2px;
      background: -webkit-gradient(linear, left bottom, left top, from(rgba(43, 8, 8, 0)), to(variables.$main-green-color));
      background: linear-gradient(to top, rgba(43, 8, 8, 0), variables.$main-green-color);
      -webkit-animation: 2s animateRight linear -1s infinite;
              animation: 2s animateRight linear -1s infinite;
    }

    &:nth-child(3) {
      bottom: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background: -webkit-gradient(linear, left top, right top, from(rgba(43, 8, 8, 0)), to(variables.$main-green-color));
      background: linear-gradient(to right, rgba(43, 8, 8, 0), variables.$main-green-color);
      -webkit-animation: 2s animateBottom linear infinite;
              animation: 2s animateBottom linear infinite;
    }

    &:nth-child(4) {
      top: 0;
      left: 0;
      height: 100%;
      width: 2px;
      background: -webkit-gradient(linear, left top, left bottom, from(rgba(43, 8, 8, 0)), to(variables.$main-green-color));
      background: linear-gradient(to bottom, rgba(43, 8, 8, 0), variables.$main-green-color);
      -webkit-animation: 2s animateLeft linear -1s infinite;
              animation: 2s animateLeft linear -1s infinite;
    }
  }
}

.horizontal-menu {
  li {
    &.black > a,
    &.blue > a,
    &.green > a,
    &.iherb > a,
    &.orange > a,
    &.pink > a,
    &.red > a {
      color: white;

      &:hover,
      &.nuxt-link-exact-active {
        color: white;
      }
    }

    &.black > a {
      background: black;

      &:hover,
      &.nuxt-link-exact-active {
        background: color.adjust(black, $lightness: 10%);
      }
    }

    &.blue > a {
      background: variables.$main-blue-color;

      &:hover,
      &.nuxt-link-exact-active {
        background: color.adjust(variables.$main-blue-color, $lightness: 10%);
      }
    }

    &.green > a {
      border: 1px solid variables.$main-green-color;
      color: variables.$main-green-color;

      &:hover,
      &.nuxt-link-exact-active {
        color: variables.$main-green-color;
      }
    }

    &.iherb > a {
      background: #458500;
      text-transform: none;

      &:hover,
      &.nuxt-link-exact-active {
        background: color.adjust(#458500, $lightness: 10%);
      }
    }

    &.orange > a {
      background: variables.$main-orange-color;

      &:hover,
      &.nuxt-link-exact-active {
        background: color.adjust(variables.$main-orange-color, $lightness: -10%);
      }
    }

    &.pink > a {
      $pink-color: #f00073;

      background: $pink-color;

      &:hover,
      &.nuxt-link-exact-active {
        background: color.adjust($pink-color, $lightness: 10%);
      }
    }

    &.red > a {
      color: red;
      border: 1px solid red;

      &:hover,
      &.nuxt-link-exact-active {
        color: red;
      }
    }

    &.sale1111 > a {
      background: #ff9305;
      color: #4a2b0d;

      &:hover,
      &.nuxt-link-exact-active {
        color: white;
      }
    }

    &:last-child {
      margin-right: 0;
    }

    a {
      background: white;
      border-radius: 12px;
      color: inherit;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      padding: 10px 16px;
      transition: background-color .25s ease, color .25s ease;

      @media screen and (max-width: 480px) {
        border-radius: 4px;
        font-size: 12px;
        line-height: 14px;
        padding: 4px 8px;
      }

      &:hover,
      &.nuxt-link-exact-active {
        color: variables.$main-green-color;
        text-decoration: none;
      }
    }
  }
}

@keyframes animateTop {
  0% {
    -webkit-transform: translateX(100%);
            transform: translateX(100%);
  }
  100% {
    -webkit-transform: translateX(-100%);
            transform: translateX(-100%);
  }
}

@keyframes animateRight {
  0% {
    -webkit-transform: translateY(100%);
            transform: translateY(100%);
  }
  100% {
    -webkit-transform: translateY(-100%);
            transform: translateY(-100%);
  }
}

@keyframes animateBottom {
  0% {
    -webkit-transform: translateX(-100%);
            transform: translateX(-100%);
  }
  100% {
    -webkit-transform: translateX(100%);
            transform: translateX(100%);
  }
}

@keyframes animateLeft {
  0% {
    -webkit-transform: translateY(-100%);
            transform: translateY(-100%);
  }
  100% {
    -webkit-transform: translateY(100%);
            transform: translateY(100%);
  }
}
</style>
